import * as React from "react"
import { TextProps } from "../"
import { Link, LinkProps } from "react-router-dom"

export interface StackNavigationView {
	title?: string
	id: string
	component: React.FC
}

interface StackNavigationComponent {
	Nav: React.FC<StackNavigationNavProps>
	Link: React.FC<LinkProps>
	Views: React.FC
}

export interface StackNavigationProps {
	initialViewId: string
	views: StackNavigationView[]
	reset?: boolean
	children?: React.ReactNode
}

interface StackNavigationContextValue {
	history: StackNavigationView[]
	currentView: StackNavigationView
	previousView?: StackNavigationView
	views: StackNavigationView[]
	goToView: (id: string) => void
	goBack: () => void
}

export const StackNavigationContext =
	React.createContext<StackNavigationContextValue>({
		history: [],
		currentView: { id: "", component: () => <React.Fragment /> },
		views: [],
		goToView: (id) => {},
		goBack: () => {},
	})

const StackNavigation: React.FC<StackNavigationProps> &
	StackNavigationComponent = (props) => {
	const findView = (id: string, views: StackNavigationView[]) =>
		views.find((view) => view.id === id) || props.views[0]

	const [history, setHistory] = React.useState<StackNavigationView[]>([
		findView(props.initialViewId, props.views),
	])
	const [currentView, setCurrentView] = React.useState(history[0])
	const [previousView, setPreviousView] = React.useState<StackNavigationView>()

	React.useEffect(() => {
		setHistory([findView(props.initialViewId, props.views)])
	}, [props.reset])

	React.useEffect(() => {
		setCurrentView(history[history.length - 1])
		setPreviousView(history[history.length - 2])
	}, [history])

	const goToView = (id: string) => {
		setHistory((prev) => [...prev, findView(id, props.views)])
	}

	const goBack = () => {
		setHistory((prev) => [...prev].splice(0, prev.length - 1))
	}

	return (
		<StackNavigationContext.Provider
			value={{
				history,
				goToView,
				goBack,
				views: props.views,
				currentView,
				previousView,
			}}
		>
			{props.children}
		</StackNavigationContext.Provider>
	)
}

interface StackNavigationNavProps extends TextProps {
	to: string
	name?: string
}

/**
 *  Nav
 *	Acts as navigation item between views
 */
StackNavigation.Nav = ((props) => {
	const { goToView } = React.useContext(StackNavigationContext)

	return (
		<button
			onClick={() => {
				console.log("to: ", props.to)

				goToView(props.to)
			}}
			style={{ display: "flex", width: "100%" }}
		>
			{props.children}
		</button>
	)
}) as React.FC<StackNavigationNavProps>

/**
 *  Link
 *	React router link component for linking to other pages
 */
StackNavigation.Link = (props) => {
	return (
		<Link style={{ display: "flex", width: "100%" }} {...props}>
			{props.children}
		</Link>
	)
}

/**
 *  Views
 *	Are where the current view should be rendered
 */
StackNavigation.Views = ((props) => {
	const { currentView } = React.useContext(StackNavigationContext)
	const CurrentView = currentView.component
	return <CurrentView />
}) as React.FC

export { StackNavigation }
